import {
  animate,
  type HTMLMotionProps,
  type PanInfo,
  useMotionValue,
  useReducedMotion,
} from "motion/react";
import type { KeyboardEvent, RefObject } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  type BubbleFrame,
  type BubbleMetrics,
  dockBubblePosition,
  getBubbleBounds,
  NUDGE_STEP,
  NUDGE_STEP_LARGE,
  nudgeBubblePosition,
  type Point,
  resolveBubblePosition,
  resolveDragRelease,
  toStoredBubblePosition,
  type Viewport,
} from "../_libs/bubble-position";
import {
  onBubblePositionReset,
  readStoredBubblePosition,
  writeStoredBubblePosition,
} from "../_libs/bubble-position-storage";

/**
 * Release spring — damping ratio ~0.77, so it overshoots the target by ~2% of
 * the distance travelled and settles in ~400ms. That proportional overshoot is
 * the entire bounce.
 *
 * The pointer's velocity is deliberately NOT fed into this spring. Velocity
 * already decides WHERE the bubble lands (`resolveDragRelease` projects the
 * flick forward), so injecting it again launches the bubble far past its
 * target — a hard flick sent it ~450px off-screen before the spring hauled it
 * back. Distance-proportional overshoot stays on screen at any flick speed.
 */
const RELEASE_SPRING = {
  type: "spring",
  stiffness: 170,
  damping: 20,
  mass: 1,
} as const;

/** Reduced motion: same spring, critically damped — snaps home with no overshoot. */
const RELEASE_SPRING_REDUCED = {
  type: "spring",
  stiffness: 420,
  damping: 42,
  mass: 1,
} as const;

/**
 * `position: fixed` resolves against the viewport MINUS a classic desktop
 * scrollbar, which `window.innerWidth` includes — using it would tuck the
 * docked bubble ~15px under the scrollbar.
 */
function getViewport(): Viewport {
  const el = document.documentElement;
  return { width: el.clientWidth, height: el.clientHeight };
}

/**
 * Re-start Motion's hover state when the bubble lands back under a stationary
 * pointer.
 *
 * Motion's hover gesture drops any `pointerenter` that arrives while a drag is
 * active (`isValidHover` in motion-dom's `hover.mjs` returns false for
 * `isDragActive()`), and its `pointerup` runs the hover-end it deferred when
 * the pointer left. So after a drag the element is hover-less, and because the
 * pointer itself never moved, no further `pointerenter` is ever emitted — the
 * bubble stays un-hovered until you move off it and back on.
 *
 * Whether the pointer is really over the bubble is decided by hit-testing the
 * release point, NOT by `:hover`: Chrome only re-evaluates `:hover` when the
 * pointer itself moves, so after a bubble springs out from (or into) a
 * stationary cursor that flag is stale in both directions. `elementFromPoint`
 * is a live hit-test, and it reports occlusion too — if something else is on
 * top at that point, the pointer is not on this bubble.
 */
function rearmHoverIfPointerIsOver(
  element: HTMLButtonElement | null,
  releasePoint: Point,
  isDraggingRef: { current: boolean },
): void {
  // A new drag started while the last one was still springing home.
  if (!element || isDraggingRef.current) return;
  if (!Number.isFinite(releasePoint.x) || !Number.isFinite(releasePoint.y))
    return;

  const under = document.elementFromPoint(releasePoint.x, releasePoint.y);
  if (!under || !element.contains(under)) return;

  element.dispatchEvent(
    new PointerEvent("pointerenter", { bubbles: false, pointerType: "mouse" }),
  );
}

export type UseDraggableBubbleOptions = {
  /**
   * Must be referentially stable — a module-level constant, not an inline
   * object literal. It is an effect dependency, so a fresh identity every render
   * would re-subscribe the resize listeners every frame.
   */
  metrics: BubbleMetrics;
  /** localStorage key for the parked position. Must be unique per bubble. */
  storageKey: string;
  /** Drag + click are inert while false. Pass the bubble's own visibility. */
  enabled: boolean;
  /** A real click — never fires as the tail end of a drag. */
  onActivate: () => void;
};

/**
 * Exactly the props this hook owns. Spread onto the bubble element AFTER any
 * `animate`/`transition` of your own and BEFORE `className` — `style` carries
 * the x/y motion values, so a later bare `style` would freeze the bubble.
 */
export type DraggableBubbleDragProps = Pick<
  HTMLMotionProps<"button">,
  | "style"
  | "drag"
  | "dragMomentum"
  | "dragElastic"
  | "dragConstraints"
  | "whileDrag"
  | "whileTap"
  | "whileHover"
  | "onPointerDown"
  | "onDragStart"
  | "onDragEnd"
  | "onKeyDown"
  | "onClick"
> & {
  "aria-keyshortcuts": string;
  ref: RefObject<HTMLButtonElement | null>;
};

export type DraggableBubble = {
  dragProps: DraggableBubbleDragProps;
};

/**
 * Drag behavior for a floating launcher bubble: tracks the pointer 1:1, then
 * springs to the nearer side on release (iOS AssistiveTouch model) so it can be
 * moved off whatever it is covering. The docked side + height persist across
 * reloads under `storageKey`.
 *
 * The consumer renders its own `motion.button` — its own icon, badge, aria text
 * and appearance — and spreads `dragProps`. Position it `fixed top-0 left-0`:
 * the x/y motion values ARE the viewport pixel position, so clamping, snapping
 * and storage all share one number and the CSS layout box never moves on resize.
 */
export function useDraggableBubble({
  metrics,
  storageKey,
  enabled,
  onActivate,
}: UseDraggableBubbleOptions): DraggableBubble {
  const reduceMotion = useReducedMotion();
  const releaseSpring = reduceMotion ? RELEASE_SPRING_REDUCED : RELEASE_SPRING;

  // Resolved lazily so the bubble is already in place on first paint — motion
  // reads motion values into the initial render style.
  const [initialPoint] = useState(() =>
    resolveBubblePosition(readStoredBubblePosition(storageKey), {
      viewport: getViewport(),
      metrics,
    }),
  );

  const x = useMotionValue(initialPoint.x);
  const y = useMotionValue(initialPoint.y);

  const [bounds, setBounds] = useState(() =>
    getBubbleBounds({ viewport: getViewport(), metrics }),
  );

  const isDraggingRef = useRef(false);
  const didDragRef = useRef(false);

  const elementRef = useRef<HTMLButtonElement | null>(null);

  /** Animate to a resolved point and persist it. Resolves once it has landed. */
  const settle = useCallback(
    (point: Point, frame: BubbleFrame) => {
      const landed = Promise.all([
        animate(x, point.x, releaseSpring).finished,
        animate(y, point.y, releaseSpring).finished,
      ]);
      writeStoredBubblePosition(
        storageKey,
        toStoredBubblePosition(point, frame),
      );
      return landed;
    },
    [x, y, releaseSpring, storageKey],
  );

  useEffect(() => {
    const reposition = () => {
      // A mid-drag resize (mobile address bar showing/hiding) must not yank
      // the bubble out from under the finger.
      if (isDraggingRef.current) return;
      const frame = { viewport: getViewport(), metrics };
      setBounds(getBubbleBounds(frame));
      const next = resolveBubblePosition(
        readStoredBubblePosition(storageKey),
        frame,
      );
      // jump, not animate: keeps the bubble glued to its edge while the window
      // is being dragged, with no re-render and no debounce.
      x.jump(next.x);
      y.jump(next.y);
    };

    const resetToDefault = () => {
      const frame = { viewport: getViewport(), metrics };
      setBounds(getBubbleBounds(frame));
      settle(resolveBubblePosition(null, frame), frame);
    };

    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("orientationchange", reposition);
    const unsubscribeReset = onBubblePositionReset(storageKey, resetToDefault);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("orientationchange", reposition);
      unsubscribeReset();
    };
  }, [x, y, settle, metrics, storageKey]);

  const handleDragEnd = useCallback(
    (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      isDraggingRef.current = false;
      const frame = { viewport: getViewport(), metrics };
      const release = resolveDragRelease(
        { x: x.get(), y: y.get() },
        info.velocity,
        frame,
      );

      // Touch has no hover state to restore, and a tap leaves a sticky
      // `:hover` behind on many mobile browsers.
      const isTouch = "pointerType" in event && event.pointerType === "touch";
      const releasePoint =
        "clientX" in event
          ? { x: event.clientX, y: event.clientY }
          : { x: Number.NaN, y: Number.NaN };

      settle(release, frame).then(() => {
        if (isTouch) return;
        rearmHoverIfPointerIsOver(
          elementRef.current,
          releasePoint,
          isDraggingRef,
        );
      });
    },
    [x, y, settle, metrics],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const frame = { viewport: getViewport(), metrics };
      const current = { x: x.get(), y: y.get() };
      const step = event.shiftKey ? NUDGE_STEP_LARGE : NUDGE_STEP;

      // Left/right dock rather than nudge, so the keyboard lands the bubble in
      // the same states a drag can — a free horizontal offset isn't persistable.
      const next =
        event.key === "ArrowUp"
          ? nudgeBubblePosition(current, { x: 0, y: -step }, frame)
          : event.key === "ArrowDown"
            ? nudgeBubblePosition(current, { x: 0, y: step }, frame)
            : event.key === "ArrowLeft"
              ? dockBubblePosition(current, "left", frame)
              : event.key === "ArrowRight"
                ? dockBubblePosition(current, "right", frame)
                : null;

      if (!next) return;
      event.preventDefault();
      settle(next, frame);
    },
    [x, y, settle, metrics],
  );

  return {
    dragProps: {
      style: { x, y },
      drag: enabled,
      // Not a feel choice: motion starts its own inertia synchronously on
      // pointerup, one frame before onDragEnd runs — leaving momentum on
      // shows a frame of the wrong animation before our spring takes over.
      dragMomentum: false,
      dragElastic: 0.1,
      dragConstraints: {
        left: bounds.minX,
        right: bounds.maxX,
        top: bounds.minY,
        bottom: bounds.maxY,
      },
      whileDrag: { scale: 1.06 },
      whileTap: { scale: 0.94 },
      // Motion only fires hover on devices that can hover, so this is inert on
      // touch. Drag and tap outrank it, which is why they read as distinct.
      whileHover: { scale: 1.1 },
      // The flag must be cleared here, not in onClick: motion schedules
      // onDragEnd via frame.postRender, so it lands AFTER the native click,
      // and a gesture ending in pointercancel fires no click at all.
      onPointerDown: () => {
        didDragRef.current = false;
      },
      onDragStart: () => {
        isDraggingRef.current = true;
        didDragRef.current = true;
      },
      onDragEnd: handleDragEnd,
      onKeyDown: handleKeyDown,
      onClick: () => {
        if (didDragRef.current) return;
        onActivate();
      },
      "aria-keyshortcuts": "ArrowUp ArrowDown ArrowLeft ArrowRight",
      ref: elementRef,
    },
  };
}
